import React, {
	useLayoutEffect,
	useMemo,
	useCallback,
	memo,
	useRef,
} from "react";
import { shallowEqual } from "react-redux";

import { Order, Car } from "../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../redux/store";
import orderPage from "../../../redux/reducers/OrdersPage";
import { ORDERS_TYPES, OrderType } from "../../../redux/constants/OrdersPage";
import { OrderStatus } from "../../../types";
import { useModelSubscribe } from "../../../hooks";
import {
	useTaxiServiceFilterAccess,
	useCarParkFilterAccess,
} from "../../../access";

const OrdersDataLoader: React.FC<OrdersDataLoader.Props> = ({ carModels }) => {
	const dispatch = useTypedDispatch();
	const { subCarParkFilterAccess } = useCarParkFilterAccess<Order.Model>();
	const { subTaxiServiceFilterAccess } =
		useTaxiServiceFilterAccess<Order.Model>();

	const { connected, authorized } = useTypedSelector((state) => state.prpc);
	const currentUserId = useTypedSelector((state) => state.account.user?.id);
	const timeToLive = useTypedSelector(
		(state) =>
			state.settings.order.preliminary.preliminaryOrderBecomesLive
				.interval,
	);
	const filtersBtns = useTypedSelector(
		(state) => state.ordersPageReducer.filtersBtns,
		shallowEqual,
	);

	const canSubscribe = useMemo(
		() => connected && authorized,
		[authorized, connected],
	);

	const carMap = useMemo(() => {
		const map = new Map<number, Car.Model>();
		for (const car of carModels) {
			map.set(car.id, car);
		}
		return map;
	}, [carModels]);

	const [optionsOrderClosed, optionsOrderAll]: Order.SubscribeOptions[] = [
		useMemo(
			() => ({
				tab: ORDERS_TYPES.CLOSED,
				dateRange: {
					from: new Date(Date.now() - 86400000),
					to: new Date(Date.now() + 86400000),
				},
			}),
			[],
		),
		useMemo(
			() => ({
				tab: ORDERS_TYPES.ALL,
				...(filtersBtns.showOnlyMyOrders &&
					currentUserId && {
						dispatcherIds: [currentUserId],
					}),
				...(filtersBtns.showOrdersWithoutExecutors && {
					hasExecutor: false,
				}),
			}),
			[filtersBtns, currentUserId],
		),
	];

	const [closedOrdersModels, allOrdersModels] = [
		useModelSubscribe(optionsOrderClosed, Order)?.models || [],
		useModelSubscribe(optionsOrderAll, Order)?.models || [],
	];

	const mapMultyOrders = useCallback(
		(orders: Order.Model[], type: OrderType): Order.Model[] => {
			const futureTime = timeToLive ? Date.now() + timeToLive : 0;

			return orders.map((order) => {
				// enrich car parks
				const enrichedCarToOrder =
					order.carToOrder?.map((carItem) => {
						const car = carMap.get(carItem.car?.id);
						return car
							? {
									...carItem,
									car: {
										...carItem.car,
										park: car.park,
									},
							  }
							: carItem;
					}) ?? [];

				// determine new stage
				const {
					stage,
					orderDate,
					moveToProgress,
					isPreliminary,
					status,
					executorToOrder,
				} = order;

				const parsedOrderDate = orderDate
					? new Date(orderDate).getTime()
					: 0;
				const isLastTime = parsedOrderDate <= futureTime;
				const isMoreTime = parsedOrderDate > futureTime;
				const isMoveVal = !!moveToProgress?.value;
				const isTypeAll = !stage && type === ORDERS_TYPES.ALL;
				const isEmptyExecutorToOrder = !executorToOrder?.length;

				const isStage: Record<Order.OrderStage, boolean> = {
					[Order.ORDERS_STAGE.LIVE]:
						(!isMoveVal && isLastTime) ||
						(status === OrderStatus.FREE_WAVE &&
							isEmptyExecutorToOrder),
					[Order.ORDERS_STAGE.PRELIMINARY]:
						isPreliminary && isMoreTime,
					[Order.ORDERS_STAGE.EXECUTABLE]: !!(
						status === OrderStatus.EXECUTING &&
						isMoveVal &&
						(!isPreliminary || isLastTime)
					),
				};

				let newStage = stage;

				if (isTypeAll) {
					switch (true) {
						case isStage[Order.ORDERS_STAGE.LIVE]:
							newStage = Order.ORDERS_STAGE.LIVE;
							break;
						case isStage[Order.ORDERS_STAGE.PRELIMINARY]:
							newStage = Order.ORDERS_STAGE.PRELIMINARY;
							break;
						case isStage[Order.ORDERS_STAGE.EXECUTABLE]:
							newStage = Order.ORDERS_STAGE.EXECUTABLE;
							break;
						default:
							break;
					}
				}

				return {
					...order,
					carToOrder: enrichedCarToOrder,
					...(newStage ? { stage: newStage } : {}),
				};
			});
		},
		[carMap, timeToLive],
	);

	const modelsClosedWithMapAndFilterBase: Order.Model[] = useMemo(() => {
		const models = mapMultyOrders(closedOrdersModels, ORDERS_TYPES.CLOSED);

		return models;
	}, [closedOrdersModels, mapMultyOrders]);

	const modelsAllWithMapAndFilterBase: Order.Model[] = useMemo(() => {
		const models = mapMultyOrders(allOrdersModels, ORDERS_TYPES.ALL);

		return models;
	}, [allOrdersModels, mapMultyOrders]);

	const distributeAndSetOrders = useCallback(
		(orders: Order.Model[]) => {
			const distributed: Record<
				Exclude<OrderType, "all" | "closed">,
				Order.Model[]
			> = {
				[ORDERS_TYPES.LIVE]: [],
				[ORDERS_TYPES.PRELIMINARY]: [],
				[ORDERS_TYPES.EXECUTABLE]: [],
			};

			for (const model of orders) {
				if (model.stage) distributed[model.stage]?.push(model);
			}

			dispatch(
				orderPage.actions.setOrdersByTab({
					tab: ORDERS_TYPES.ALL,
					data: {
						items: orders,
						count: orders.length,
						pagination: {
							offset: orders.length,
							count: orders.length,
						},
						loaded: true,
					},
				}),
			);

			for (const key of Object.keys(distributed) as OrderType[]) {
				dispatch(
					orderPage.actions.setOrdersByTab({
						tab: key,
						data: {
							items: distributed[key],
							count: distributed[key].length,
							pagination: {
								offset: distributed[key].length,
								count: distributed[key].length,
							},
							loaded: true,
						},
					}),
				);
			}
		},
		[dispatch],
	);

	const handleOrders = useCallback(
		(data: Order.Model[], type: OrderType) => {
			if (!canSubscribe) return;
			let models = data;

			models = subTaxiServiceFilterAccess({
				models,
				branchPath: "taxiService.id",
			});

			models = subCarParkFilterAccess({
				models,
				branchPath: "carToOrder",
				branchPath2: "car.park.id",
				isOrderLogic: type === ORDERS_TYPES.ALL,
			}); /* .sort((a, b) => b.id - a.id); */

			if (type === ORDERS_TYPES.CLOSED) {
				dispatch(
					orderPage.actions.setOrdersByTab({
						tab: type,
						data: {
							items: models,
							count: models.length,
							pagination: {
								offset: models.length,
								count: models.length,
							},
							loaded: true,
						},
					}),
				);
			} else if (type === ORDERS_TYPES.ALL) {
				distributeAndSetOrders(models);
			}
		},
		[
			canSubscribe,
			subTaxiServiceFilterAccess,
			subCarParkFilterAccess,
			dispatch,
			distributeAndSetOrders,
		],
	);

	const prevClosedModels = useRef<Order.Model[]>([]);

	useLayoutEffect(() => {
		const newModels = modelsClosedWithMapAndFilterBase;

		const changed =
			prevClosedModels.current.length !== newModels.length ||
			prevClosedModels.current.some(
				(model, i) => model.id !== newModels[i].id,
			);

		if (changed) {
			handleOrders(newModels, ORDERS_TYPES.CLOSED);
			prevClosedModels.current = newModels;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modelsClosedWithMapAndFilterBase]);

	useLayoutEffect(() => {
		handleOrders(modelsAllWithMapAndFilterBase, ORDERS_TYPES.ALL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modelsAllWithMapAndFilterBase]);

	return null;
};

declare namespace OrdersDataLoader {
	interface Props {
		carModels: Car.Model[];
	}
}

export default memo(OrdersDataLoader);
